import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronsUpDown, Search, X } from 'lucide-react';
import { cx } from '@/lib/cx';
import { useFieldControl } from './Field';
import { Spinner } from './Spinner';

export interface ComboOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
  leading?: ReactNode;
}

interface BaseProps {
  options: ComboOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  loading?: boolean;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
  size?: 'sm' | 'md';
  className?: string;
  /** Show a clear (×) control. */
  clearable?: boolean;
}

interface SingleProps extends BaseProps {
  multiple?: false;
  value: string | null | undefined;
  onChange: (v: string | null) => void;
}
interface MultiProps extends BaseProps {
  multiple: true;
  value: string[];
  onChange: (v: string[]) => void;
}

export type ComboboxProps = SingleProps | MultiProps;

/**
 * Searchable select. Trigger button → popover with a filter input (role=combobox) and a
 * listbox; ↑/↓ moves, Enter selects, Esc closes, Home/End jump. Multi mode keeps the popover
 * open and shows a compact summary in the trigger.
 */
export function Combobox(props: ComboboxProps) {
  const { options, placeholder = 'Select…', searchPlaceholder = 'Search…', emptyText = 'No matches', loading, disabled, size = 'md', className, clearable } = props;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listId = useId();
  const listRef = useRef<HTMLUListElement>(null);
  const a11y = useFieldControl({ id: props.id });

  const selected = props.multiple ? props.value : props.value ? [props.value] : [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q) || o.description?.toLowerCase().includes(q)) : options;
  }, [options, query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      const idx = options.findIndex((o) => selected.includes(o.value));
      setActive(Math.max(0, idx));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    setActive((a) => Math.min(a, Math.max(0, filtered.length - 1)));
  }, [filtered.length]);

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const choose = (opt: ComboOption | undefined) => {
    if (!opt || opt.disabled) return;
    if (props.multiple) {
      const next = props.value.includes(opt.value) ? props.value.filter((v) => v !== opt.value) : [...props.value, opt.value];
      props.onChange(next);
    } else {
      props.onChange(opt.value);
      setOpen(false);
    }
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActive(filtered.length - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(filtered[active]);
    }
  };

  const labels = options.filter((o) => selected.includes(o.value)).map((o) => o.label);
  const summary = labels.length === 0 ? null : props.multiple ? (labels.length <= 2 ? labels.join(', ') : `${labels.slice(0, 1).join(', ')} +${labels.length - 1}`) : labels[0];
  const hasValue = selected.length > 0;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <div className={cx('combo', className)}>
        <Popover.Trigger asChild disabled={disabled}>
          <button
            type="button"
            className={cx('input', 'combo-trigger', `input-${size}`, !hasValue && 'is-placeholder')}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={props['aria-label']}
            id={a11y.id}
            aria-describedby={a11y['aria-describedby']}
            aria-invalid={a11y['aria-invalid']}
          >
            <span className="truncate">{summary ?? placeholder}</span>
            {props.multiple && labels.length > 0 && <span className="combo-count tabular">{labels.length}</span>}
            <ChevronsUpDown size={15} aria-hidden className="combo-chevron" />
          </button>
        </Popover.Trigger>
        {clearable && hasValue && !disabled && (
          <button type="button" className="combo-clear" aria-label="Clear selection" onClick={() => (props.multiple ? props.onChange([]) : props.onChange(null))}>
            <X size={13} />
          </button>
        )}
      </div>
      <Popover.Portal>
        <Popover.Content className="popover combo-pop" align="start" sideOffset={4} collisionPadding={8} onOpenAutoFocus={(e) => e.preventDefault()}>
          <div className="combo-search">
            <Search size={14} aria-hidden />
            <input
              autoFocus
              role="combobox"
              aria-expanded
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={filtered[active] ? `${listId}-${active}` : undefined}
              aria-label={searchPlaceholder}
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={onKey}
            />
            {loading && <Spinner size={13} />}
          </div>
          <ul className="combo-list" role="listbox" id={listId} aria-multiselectable={props.multiple || undefined} ref={listRef}>
            {filtered.length === 0 && <li className="combo-empty">{loading ? 'Loading…' : emptyText}</li>}
            {filtered.map((o, i) => {
              const isSel = selected.includes(o.value);
              return (
                <li
                  key={o.value}
                  id={`${listId}-${i}`}
                  data-index={i}
                  role="option"
                  aria-selected={isSel}
                  aria-disabled={o.disabled || undefined}
                  className={cx('combo-option', i === active && 'is-active', isSel && 'is-selected', o.disabled && 'is-disabled')}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(o)}
                >
                  <span className={cx('combo-check', props.multiple && 'is-box')} aria-hidden>
                    {isSel && <Check size={13} strokeWidth={2.5} />}
                  </span>
                  {o.leading}
                  <span className="combo-option-text">
                    <span className="truncate">{o.label}</span>
                    {o.description && <span className="combo-option-desc truncate">{o.description}</span>}
                  </span>
                </li>
              );
            })}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
